import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Compass, ShoppingCart, Video, BookOpen, 
  CreditCard, Settings, LogOut, MessageCircle, Shield, ChevronDown, Bell, Sparkles, Lightbulb, UserCheck, Wand2,
  Package, Briefcase, Wrench, Send, Bookmark, Users, BarChart3, Heart, Share2, Globe, GraduationCap, Clapperboard, Gift, Handshake, TrendingUp, HelpCircle, DollarSign, UserPlus, Zap, Bot
} from 'lucide-react';
import { useAuth, api } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { Logo } from '../Logo';
import { GridPattern } from '../BackgroundEffects';
import { useTour } from '../ProductTour';
import { AccountSwitcher } from '../AccountSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// User Avatar component with error handling
const UserAvatar = ({ avatar, name, size = 'sm', className = '' }) => {
  const [imageError, setImageError] = useState(false);
  const initial = name?.charAt(0).toUpperCase() || 'U';
  
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base'
  };
  
  const handleError = useCallback(() => {
    setImageError(true);
  }, []);
  
  // Reset error state when avatar URL changes
  useEffect(() => {
    setImageError(false);
  }, [avatar]);
  
  if (avatar && !imageError) {
    return (
      <img 
        src={avatar} 
        alt={name}
        className={cn(sizeClasses[size], "rounded-full object-cover border-2 border-white/20", className)}
        onError={handleError}
      />
    );
  }
  
  return (
    <div className={cn(
      sizeClasses[size],
      "rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-black font-bold",
      className
    )}>
      {initial}
    </div>
  );
};

// Business owner navigation - full access to business tools
const BUSINESS_NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/agent', label: 'AGI Workhorse™', icon: Bot },
  { path: '/cinematic-ads', label: 'Cinematic Ads', icon: Clapperboard },
  { path: '/diy-tools', label: 'Do It Yourself', icon: Wand2 },
  { path: '/gifts', label: 'Gift Portal', icon: Gift },
  { 
    path: '/publishing', 
    label: 'Publish', 
    icon: Send,
    children: [
      { path: '/publishing', label: 'Publishing Dashboard', icon: Send },
      { path: '/analytics', label: 'Analytics', icon: BarChart3 },
      { path: '/ranking', label: 'Ranking System', icon: TrendingUp },
      { path: '/affiliate', label: 'Affiliate Program', icon: DollarSign },
    ]
  },
];

// Consumer navigation - limited to community features (no AI Knowledge)
const CONSUMER_NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/directory', label: 'Directory', icon: Globe },
  { path: '/affiliate', label: 'Affiliate Program', icon: DollarSign },
];

// Business Community/Social items
const BUSINESS_COMMUNITY_ITEMS = [
  { path: '/messages', label: 'Messages', icon: MessageCircle },
  { path: '/mentions', label: 'Mentions', icon: Users },
  { path: '/bookmarks', label: 'Bookmarks', icon: Bookmark },
  { path: '/my-listing', label: 'My Business Listing', icon: Briefcase },
];

// Consumer Community/Social items
const CONSUMER_COMMUNITY_ITEMS = [
  { path: '/messages', label: 'Messages', icon: MessageCircle },
  { path: '/mentions', label: 'Mentions', icon: Users },
  { path: '/bookmarks', label: 'Bookmarks', icon: Bookmark },
];

// Business user account items
const BUSINESS_ACCOUNT_ITEMS = [
  { path: '/onboarding', label: 'Onboarding', icon: UserCheck },
  { path: '/training', label: 'My Training', icon: GraduationCap },
  { path: '/affiliate/training', label: 'Affiliate Training', icon: DollarSign },
  { path: '/leads', label: 'Leads Dashboard', icon: UserPlus },
  { path: '/team', label: 'Team Members', icon: Users },
  { path: '/orders', label: 'My Orders', icon: ShoppingCart },
  { path: '/billing', label: 'Billing & Credits', icon: CreditCard },
  { path: '/security/mfa', label: 'Two-Factor Auth', icon: Shield },
  { path: '/settings', label: 'Settings', icon: Settings },
  { path: '/knowledge', label: 'AI Knowledge', icon: BookOpen },
];

// Consumer user account items
const CONSUMER_ACCOUNT_ITEMS = [
  { path: '/affiliate/training', label: 'Affiliate Training', icon: DollarSign },
  { path: '/security/mfa', label: 'Two-Factor Auth', icon: Shield },
  { path: '/settings', label: 'Settings', icon: Settings },
];

const ADMIN_ITEMS = [
  { path: '/admin', label: 'Admin Dashboard', icon: Shield },
  { path: '/admin/orders', label: 'Manage Orders', icon: ShoppingCart },
  { path: '/admin/appointments', label: 'Appointments', icon: Video },
  { path: '/admin/team', label: 'Team', icon: MessageCircle },
  { path: '/admin/services', label: 'Services', icon: Compass },
  { path: '/admin/service-requests', label: 'Service Requests', icon: Lightbulb },
  { path: '/admin/partner-inquiries', label: 'Partner Inquiries', icon: Handshake },
];

export const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { startTour } = useTour();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const isAdmin = user?.role === 'admin' || user?.role === 'pod_team_member';
  const isConsumer = user?.role === 'consumer';
  
  // Select navigation items based on user role
  const NAV_ITEMS = isConsumer ? CONSUMER_NAV_ITEMS : BUSINESS_NAV_ITEMS;
  const COMMUNITY_ITEMS = isConsumer ? CONSUMER_COMMUNITY_ITEMS : BUSINESS_COMMUNITY_ITEMS;
  const USER_ACCOUNT_ITEMS = isConsumer ? CONSUMER_ACCOUNT_ITEMS : BUSINESS_ACCOUNT_ITEMS;
  
  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const [notifRes, countRes] = await Promise.all([
          api.getNotifications(),
          api.getUnreadCount()
        ]);
        setNotifications(notifRes.data.slice(0, 10)); // Last 10 notifications
        setUnreadCount(countRes.data.count);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    
    fetchNotifications();
    // Poll every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await api.markNotificationRead(notification.id);
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
      );
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };
  
  const handleMarkAllRead = async () => {
    await api.markAllNotificationsRead();
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-heavy">
        <div className="max-w-[1800px] mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo and Account Switcher */}
            <div className="flex items-center gap-3">
              <Link to="/dashboard" className="flex items-center gap-2">
                <Logo size="sm" showText={true} />
              </Link>
              
              {/* Account/Sub-Account Switcher - Admin and team members only */}
              {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'pod_team_member') && (
                <AccountSwitcher />
              )}
            </div>
            
            {/* Main Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {NAV_ITEMS.map((item) => {
                // Map item paths to tour identifiers
                const tourId = {
                  '/diy-tools': 'nav-diy',
                  '/ranking': 'nav-ranking',
                  '/publishing': 'nav-publish',
                  '/affiliate': 'nav-affiliate',
                  '/gift-portal': 'nav-gift-portal',
                  '/agent': 'nav-agent',
                }[item.path];
                
                // Handle dropdown items
                if (item.children) {
                  const isActive = item.children.some(child => location.pathname === child.path);
                  return (
                    <DropdownMenu key={item.path}>
                      <DropdownMenuTrigger 
                        className={cn(
                          "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                          isActive
                            ? "bg-white/10 text-white"
                            : "text-gray-400 hover:text-white hover:bg-white/5"
                        )}
                        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                        data-tour={tourId}
                      >
                        <item.icon className="w-4 h-4" />
                        {item.label}
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="bg-card border-white/10 min-w-[180px]">
                        {item.children.map((child) => (
                          <DropdownMenuItem 
                            key={child.path} 
                            onClick={() => navigate(child.path)}
                            className={cn(
                              "cursor-pointer",
                              location.pathname === child.path && "bg-white/10"
                            )}
                          >
                            <child.icon className="w-4 h-4 mr-2" />
                            {child.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                      location.pathname === item.path || (item.path === '/marketplace' && location.pathname.startsWith('/marketplace'))
                        ? "bg-white/10 text-white"
                        : "text-gray-400 hover:text-white hover:bg-white/5"
                    )}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    data-tour={tourId}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}

              {/* My Business Listing - Dropdown with Tracking Settings */}
              <DropdownMenu>
                <DropdownMenuTrigger 
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                    (location.pathname === '/my-listing' || location.pathname === '/tracking-settings')
                      ? "bg-white/10 text-white"
                      : "text-gray-400 hover:text-white hover:bg-white/5"
                  )}
                  data-testid="my-listing-link"
                >
                  <Briefcase className="w-4 h-4" />
                  My Business Listing
                  <ChevronDown className="w-3 h-3 ml-1" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-card border-white/10 min-w-[200px]">
                  <DropdownMenuItem 
                    onClick={() => navigate('/my-listing')}
                    className={cn(
                      "cursor-pointer",
                      location.pathname === '/my-listing' && "bg-white/10"
                    )}
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    Business Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/tracking-settings')}
                    className={cn(
                      "cursor-pointer",
                      location.pathname === '/tracking-settings' && "bg-white/10"
                    )}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Tracking & Retargeting
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
            
            {/* User Menu */}
            <div className="flex items-center gap-3">
              {/* Notification Bell */}
              <DropdownMenu>
                <DropdownMenuTrigger className="relative p-2 rounded-lg hover:bg-white/5 transition-colors" data-testid="notification-bell" data-tour="notification-bell">
                  <Bell className="w-5 h-5 text-gray-400" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-accent text-xs font-bold flex items-center justify-center text-black">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-card border-white/10 w-80 max-h-96 overflow-y-auto">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
                    <span className="font-medium text-white text-sm">Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} className="text-xs text-primary hover:underline">
                        Mark all read
                      </button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <div className="px-3 py-6 text-center text-gray-500 text-sm">
                      No notifications yet
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <DropdownMenuItem 
                        key={notif.id}
                        onClick={() => handleNotificationClick(notif)}
                        className={cn(
                          "flex flex-col items-start gap-1 px-3 py-2 cursor-pointer hover:bg-white/10 focus:bg-white/10",
                          !notif.read && "bg-white/5"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {!notif.read && <div className="w-2 h-2 rounded-full bg-accent flex-shrink-0" />}
                          <span className="font-medium text-white text-sm">{notif.title}</span>
                        </div>
                        <p className="text-xs text-gray-400 line-clamp-2">{notif.body}</p>
                        <span className="text-xs text-gray-500">{new Date(notif.created_at).toLocaleString()}</span>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors" data-tour="user-menu">
                  <UserAvatar avatar={user?.avatar} name={user?.name} size="sm" />
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-card border-white/10 w-56">
                  <div className="px-3 py-2 border-b border-white/10 flex items-center gap-3">
                    <UserAvatar avatar={user?.avatar} name={user?.name} size="md" />
                    <div>
                      <p className="text-sm font-medium text-white">{user?.name}</p>
                      <p className="text-xs text-gray-400">{user?.email}</p>
                    </div>
                  </div>
                  
                  {/* Admin Section - Only for admins */}
                  {isAdmin && (
                    <>
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Admin
                      </div>
                      {ADMIN_ITEMS.map((item) => (
                        <DropdownMenuItem 
                          key={item.path}
                          onClick={() => navigate(item.path)} 
                          className="cursor-pointer"
                        >
                          <item.icon className="w-4 h-4 mr-2 text-amber-400" />
                          {item.label}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator className="bg-white/10" />
                    </>
                  )}
                  
                  {/* Account Section */}
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Account
                  </div>
                  <DropdownMenuItem 
                    onClick={() => navigate('/knowledge')} 
                    className="cursor-pointer"
                    data-testid="ai-concierge-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2 text-primary" />
                    AI Concierge
                  </DropdownMenuItem>
                  {USER_ACCOUNT_ITEMS.map((item) => (
                    <DropdownMenuItem 
                      key={item.path}
                      onClick={() => navigate(item.path)} 
                      className="cursor-pointer"
                    >
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-400">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>
      
      {/* Mobile Bottom Navigation - Horizontally Scrollable */}
      <nav 
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 glass-heavy border-t border-white/10"
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <div 
          className="flex items-center overflow-x-auto scrollbar-hide py-2 px-2 gap-1 touch-pan-x"
          style={{ overscrollBehavior: 'contain' }}
        >
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors flex-shrink-0 min-w-[70px]",
                location.pathname === item.path
                  ? "text-primary bg-primary/10"
                  : "text-gray-500"
              )}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] whitespace-nowrap">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>
      
      {/* Main Content */}
      <main className="pt-20 pb-20 md:pb-8 min-h-screen relative overflow-x-hidden">
        {/* Subtle background grid for all pages */}
        <GridPattern className="opacity-30 fixed inset-0" />
        <div className="relative z-10">
          {children}
        </div>
      </main>
    </div>
  );
};
